/**
 * TRL Teams Tab
 *
 * Two screens behind one tab, chosen by lifecycle state, because the admin's job
 * changes completely when the game starts:
 *
 *  - **Setup** (`draft` → `pre_registration` → `lobby`): build the teams — create,
 *    name, colour, staff and assign captains. {@link TrlTeamSetupView}
 *  - **Live** (`active` → `round_ending`): watch the whole field and unstick it —
 *    per-player connection, scanner activity and location, plus the overrides.
 *    {@link TrlLiveDashboard}
 *  - **Over** (`completed` / `abandoned`): the live readout, overrides removed.
 *
 * This component owns the data derivation and every socket mutation; the two views
 * are presentational. Team state (`game.trlTeams`, `trlTeamProgress`, …) arrives on
 * the Game object via GAME_STATE_UPDATED — admins receive the UNMASKED snapshot,
 * so rival fog-of-war is fully visible here. Scanner activity and player locations
 * are the two things NOT on that snapshot and come from their own admin hooks.
 */

import { useState, useMemo, useCallback } from 'react';
import { getEnabledPlayers } from '@swtorbit/shared-react-hooks';
import {
  GameType,
  DEFAULT_TRL_TEAM_NAME_CONFIG,
  resolveAvailableTeamNames,
  resolveTrlTriangulationConfig,
  TRL_SIMULTANEOUS_MIN_TEAM_SIZE,
  getTeamProgressLevel,
  hasTeamReachedCheckpointLevel,
  pickFirstFreeTrlTeamColor,
  TRL_SABOTAGE_DEFINITIONS,
  enabledTrlSabotageTypes,
  type TrlStalemateClueKind,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from '../../../contexts/GameContext';
import { useTrlContext } from '../../../contexts/TrlContext';
import { useTrlAdminActivity } from '../../../hooks/useTrlAdminActivity';
import { usePlayerLocations } from '@swtorbit/shared-react-hooks';
import {
  CheckpointPanel,
  DiscordResyncPanel,
  DisruptionLogPanel,
  SabotageLogPanel,
  type CheckpointStatus,
} from './StatusPanels';
import type { TrlSabotagePlacementSummary } from './TeamCard';
import { TrlTeamSetupView } from './TrlTeamSetupView';
import { TrlLiveDashboard } from './TrlLiveDashboard';

export function TrlTeamsTab() {
  const { selectedGame } = useGameContext();
  const {
    createTeam,
    updateTeam,
    deleteTeam,
    assignPlayer,
    removePlayer,
    setTeamCaptain,
    updateTeamNameConfig,
    releaseCheckpoint,
    clearDisruption,
    resyncDiscord,
    forceClearNode,
    setCurrentNode,
    markTeamFinished,
    revealClue,
    resendSnapshot,
  } = useTrlContext();

  const [pickedCreateColor, setPickedCreateColor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isResyncing, setIsResyncing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);

  const enabledPlayers = useMemo(() => getEnabledPlayers(selectedGame), [selectedGame?.players]);

  const teams = useMemo(() => selectedGame?.trlTeams ?? [], [selectedGame?.trlTeams]);
  const config = selectedGame?.trlTeamNameConfig ?? DEFAULT_TRL_TEAM_NAME_CONFIG;

  const status = selectedGame?.status;
  const inLobby = status === 'lobby';
  const isRunning = status === 'active' || status === 'round_ending';
  const isOver = status === 'completed' || status === 'abandoned';
  // The live view serves both a running game and a finished one; only a running
  // game can still be changed.
  const showLive = isRunning || isOver;

  const roomCode = selectedGame?.roomCode;

  // ── Live-only data (not on the game snapshot) ────────────────────────────
  const teamIds = useMemo(() => teams.map((t) => t.id), [teams]);
  const activity = useTrlAdminActivity({
    enabled: isRunning,
    roomCode: roomCode ?? null,
    teamIds,
  });
  const locations = usePlayerLocations({
    enabled: isRunning,
    roomCode: roomCode ?? null,
  });

  // ── Derivations shared by both views ─────────────────────────────────────
  const progressByTeam = useMemo(
    () => new Map((selectedGame?.trlTeamProgress ?? []).map((p) => [p.teamId, p])),
    [selectedGame?.trlTeamProgress]
  );

  const nodeLabelById = useCallback(
    (nodeId: string) => selectedGame?.trlMap?.nodes.find((n) => n.id === nodeId)?.label ?? nodeId,
    [selectedGame?.trlMap?.nodes]
  );

  const teamNameById = useCallback(
    (teamId: string) => teams.find((t) => t.id === teamId)?.name ?? teamId,
    [teams]
  );

  // Lobby-only per-team start-planet readout: each team's captain-selected
  // starting planet, resolved to its node label.
  const startLabelByTeam = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const p of selectedGame?.trlTeamProgress ?? []) {
      map.set(p.teamId, p.startNodeId ? nodeLabelById(p.startNodeId) : null);
    }
    return map;
  }, [selectedGame?.trlTeamProgress, nodeLabelById]);

  // Live checkpoint hold, if any. Admins receive the unmasked snapshot, so each
  // team's round is derived from its real cleared planets — the same rule the
  // server uses to decide when to release.
  const checkpointStatus = useMemo<CheckpointStatus | null>(() => {
    const map = selectedGame?.trlMap;
    const progress = selectedGame?.trlTeamProgress ?? [];
    if (!map || selectedGame?.status !== 'active') return null;
    const held = progress.filter((p) => p.waitingAtCheckpoint);
    if (held.length === 0) return null;

    const roundOf = (clearedNodeIds: string[]) => getTeamProgressLevel(map.nodes, map.edges, clearedNodeIds);
    const level = Math.min(...held.map((p) => roundOf(p.clearedNodeIds)));
    const arrived = (p: (typeof progress)[number]) =>
      hasTeamReachedCheckpointLevel({ finished: p.finished, roundsCleared: roundOf(p.clearedNodeIds) }, level);

    return {
      level,
      heldTeamNames: held.map((p) => teamNameById(p.teamId)),
      waitingOnTeamNames: progress.filter((p) => !arrived(p)).map((p) => teamNameById(p.teamId)),
      arrivedCount: progress.filter(arrived).length,
      totalCount: progress.length,
    };
  }, [selectedGame?.trlMap, selectedGame?.trlTeamProgress, selectedGame?.status, teamNameById]);

  // Simultaneous triangulation broadcast needs one player per triangulation
  // point, so it imposes a per-team floor the admin must satisfy before the game
  // will start. No minimum otherwise.
  const minMembers = resolveTrlTriangulationConfig(selectedGame?.trlTriangulationConfig)
    .simultaneousBroadcast
    ? TRL_SIMULTANEOUS_MIN_TEAM_SIZE
    : null;

  // Disruption: admins receive every record (unmasked snapshot).
  const disruptions = useMemo(() => selectedGame?.trlDisruptions ?? [], [selectedGame?.trlDisruptions]);
  const activeBlocks = useMemo(
    () => new Map(disruptions.filter((r) => r.clearedAt == null).map((r) => [r.victimPlayerId, r])),
    [disruptions]
  );

  // Sabotage: admins receive every placement and effect (unmasked snapshot).
  const sabotageEnabled = enabledTrlSabotageTypes(selectedGame?.trlSabotageConfig).length > 0;
  const sabotageEffects = useMemo(() => selectedGame?.trlSabotageEffects ?? [], [selectedGame?.trlSabotageEffects]);
  // The type travels alongside the text rather than being baked into it, so the
  // card can draw the sabotage's artwork the way every other surface does.
  const sabotagePlacementByTeam = useMemo(() => {
    const map = new Map<string, TrlSabotagePlacementSummary>();
    for (const p of selectedGame?.trlSabotagePlacements ?? []) {
      const def = TRL_SABOTAGE_DEFINITIONS[p.type];
      map.set(p.teamId, { type: p.type, label: `${def.label} · ${nodeLabelById(p.nodeId)}` });
    }
    return map;
  }, [selectedGame?.trlSabotagePlacements, nodeLabelById]);

  const usedNames = useMemo(() => new Set(teams.map((t) => t.name)), [teams]);

  // Colour for the next team: the admin's pick while it is still free,
  // otherwise the first free palette colour.
  const createColor = useMemo(() => {
    const used = new Set(teams.map((t) => t.color?.toLowerCase()));
    if (pickedCreateColor && !used.has(pickedCreateColor.toLowerCase())) return pickedCreateColor;
    return pickFirstFreeTrlTeamColor(teams.map((t) => t.color));
  }, [teams, pickedCreateColor]);

  // Names available to create a new team (curated pool minus already-used).
  const availableNames = useMemo(
    () => resolveAvailableTeamNames(config).filter((name) => !usedNames.has(name)),
    [config, usedNames]
  );

  const playerNameById = useCallback(
    (playerId: string): string => {
      const player = selectedGame?.players.find((p) => p.playerId === playerId);
      return player?.characterName || playerId.slice(0, 8);
    },
    [selectedGame?.players]
  );

  const assignedPlayerIds = useMemo(
    () => new Set(teams.flatMap((t) => t.members.map((m) => m.playerId))),
    [teams]
  );

  const unassignedPlayers = useMemo(
    () =>
      enabledPlayers
        .filter((p) => !assignedPlayerIds.has(p.playerId))
        .map((p) => ({ playerId: p.playerId, name: p.characterName || p.playerId.slice(0, 8) })),
    [enabledPlayers, assignedPlayerIds]
  );

  // ── Mutations ────────────────────────────────────────────────────────────
  const handleCreate = useCallback(
    (name: string) => {
      if (!roomCode || !name) return;
      setError(null);
      createTeam({ roomCode, name, color: createColor }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to create team');
        else setPickedCreateColor(undefined);
      });
    },
    [roomCode, createColor, createTeam]
  );

  const handleRename = useCallback(
    (teamId: string, name: string) => {
      if (!roomCode) return;
      setError(null);
      updateTeam({ roomCode, teamId, name }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to rename team');
        else if (resp.discordError) setError(resp.discordError);
      });
    },
    [roomCode, updateTeam]
  );

  const handleRecolor = useCallback(
    (teamId: string, color: string) => {
      if (!roomCode) return;
      setError(null);
      updateTeam({ roomCode, teamId, color }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to change team colour');
      });
    },
    [roomCode, updateTeam]
  );

  const handleDelete = useCallback(
    (teamId: string) => {
      if (!roomCode) return;
      if (!window.confirm('Delete this team? This cannot be undone.')) return;
      setError(null);
      deleteTeam({ roomCode, teamId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to delete team');
      });
    },
    [roomCode, deleteTeam]
  );

  const handleAssign = useCallback(
    (teamId: string, playerId: string) => {
      if (!roomCode) return;
      setError(null);
      assignPlayer({ roomCode, teamId, playerId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to add player');
      });
    },
    [roomCode, assignPlayer]
  );

  const handleRemove = useCallback(
    (teamId: string, playerId: string) => {
      if (!roomCode) return;
      setError(null);
      removePlayer({ roomCode, teamId, playerId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to remove player');
      });
    },
    [roomCode, removePlayer]
  );

  const handleSetCaptain = useCallback(
    (teamId: string, playerId: string) => {
      if (!roomCode) return;
      setError(null);
      setTeamCaptain({ roomCode, teamId, playerId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to set captain');
      });
    },
    [roomCode, setTeamCaptain]
  );

  const handleReleaseCheckpoint = useCallback(() => {
    if (!roomCode) return;
    if (
      !window.confirm(
        'Release the checkpoint now? Held teams will continue without waiting for the teams still to arrive.'
      )
    ) {
      return;
    }
    setError(null);
    releaseCheckpoint({ roomCode }, (resp) => {
      if (!resp.success) setError(resp.error || 'Failed to release the checkpoint');
    });
  }, [roomCode, releaseCheckpoint]);

  /**
   * Surface the Discord recovery prompt only when there is something to fix:
   * the game is running, Discord is linked, and at least one team has no voice
   * channel. Before `active` the channels don't exist yet by design, and after
   * the game ends they've been torn down.
   */
  const discordResyncStatus = useMemo(() => {
    if (!isRunning || !selectedGame?.discord?.guildId || teams.length === 0) return null;
    const missingCount = teams.filter((t) => !t.discordChannelId).length;
    if (missingCount === 0) return null;
    return { missingCount, totalCount: teams.length };
  }, [isRunning, selectedGame?.discord?.guildId, teams]);

  const handleResyncDiscord = useCallback(() => {
    if (!roomCode) return;
    setError(null);
    setResyncMessage(null);
    setIsResyncing(true);
    resyncDiscord({ roomCode }, (resp) => {
      setIsResyncing(false);
      if (!resp.success) {
        setError(resp.error || 'Failed to re-sync Discord channels');
        return;
      }
      setResyncMessage(
        resp.failedTeams
          ? `${resp.syncedTeams ?? 0} team(s) have channels; ${resp.failedTeams} still failed — check the bot's permissions in the server.`
          : `All ${resp.syncedTeams ?? 0} team(s) now have channels.`
      );
    });
  }, [roomCode, resyncDiscord]);

  const handleClearBlock = useCallback(
    (victimPlayerId: string) => {
      if (!roomCode) return;
      setError(null);
      clearDisruption({ roomCode, victimPlayerId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to clear the block');
      });
    },
    [roomCode, clearDisruption]
  );

  const handleSaveNames = useCallback(
    (disabledCoreNames: string[], customNames: string[]) => {
      if (!roomCode) return;
      setError(null);
      updateTeamNameConfig({ roomCode, disabledCoreNames, customNames }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to save team names');
      });
    },
    [roomCode, updateTeamNameConfig]
  );

  // ── Live overrides ───────────────────────────────────────────────────────
  const handleForceClear = useCallback(
    (teamId: string, nodeId?: string) => {
      if (!roomCode) return;
      setError(null);
      forceClearNode({ roomCode, teamId, nodeId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to finish the round');
      });
    },
    [roomCode, forceClearNode]
  );

  const handleSetCurrentNode = useCallback(
    (teamId: string, nodeId: string) => {
      if (!roomCode) return;
      setError(null);
      setCurrentNode({ roomCode, teamId, nodeId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to move the team');
      });
    },
    [roomCode, setCurrentNode]
  );

  const handleMarkFinished = useCallback(
    (teamId: string) => {
      if (!roomCode) return;
      setError(null);
      markTeamFinished({ roomCode, teamId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to finish the team');
      });
    },
    [roomCode, markTeamFinished]
  );

  const handleRevealClue = useCallback(
    (teamId: string, clue: TrlStalemateClueKind) => {
      if (!roomCode) return;
      setError(null);
      revealClue({ roomCode, teamId, clue }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to reveal the clue');
      });
    },
    [roomCode, revealClue]
  );

  const handleResendSnapshot = useCallback(
    (teamId: string) => {
      if (!roomCode) return;
      setError(null);
      setResyncMessage(null);
      resendSnapshot({ roomCode, teamId }, (resp) => {
        if (!resp.success) setError(resp.error || 'Failed to re-push the state');
        else setResyncMessage(`State re-pushed to ${resp.recipients ?? 0} client(s).`);
      });
    },
    [roomCode, resendSnapshot]
  );

  // ── Render ───────────────────────────────────────────────────────────────
  if (!selectedGame) {
    return (
      <div className="text-white">
        <h2>Teams</h2>
        <p>Select a game from the dropdown above to manage teams.</p>
      </div>
    );
  }

  if (selectedGame.gameType !== GameType.TRAILBLAZERS) {
    return (
      <div className="text-white">
        <h2>Teams</h2>
        <p>Teams are only available for Trailblazers games.</p>
      </div>
    );
  }

  return (
    <div className="text-white d-flex flex-column gap-sm">
      {error && (
        <div className="bg-space bg-o-50 border-md border-red rounded-sm p-xs d-flex justify-content-between align-items-center">
          <span className="text-red fs-sm">{error}</span>
          <button className="btn btn--outline btn--slate btn--sm" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {checkpointStatus && <CheckpointPanel status={checkpointStatus} onRelease={handleReleaseCheckpoint} />}

      {discordResyncStatus && (
        <DiscordResyncPanel
          missingCount={discordResyncStatus.missingCount}
          totalCount={discordResyncStatus.totalCount}
          isResyncing={isResyncing}
          message={resyncMessage}
          onResync={handleResyncDiscord}
        />
      )}

      {showLive ? (
        <TrlLiveDashboard
          teams={teams}
          progressByTeam={progressByTeam}
          map={selectedGame.trlMap}
          players={selectedGame.players}
          activity={activity}
          locations={locations}
          activeBlocks={activeBlocks}
          playerNameById={playerNameById}
          nodeLabelById={nodeLabelById}
          unassignedPlayers={unassignedPlayers}
          canOverride={isRunning}
          onForceClear={handleForceClear}
          onSetCurrentNode={handleSetCurrentNode}
          onMarkFinished={handleMarkFinished}
          onRevealClue={handleRevealClue}
          onResendSnapshot={handleResendSnapshot}
          onAssign={handleAssign}
          onRemove={handleRemove}
          onSetCaptain={handleSetCaptain}
          onClearBlock={handleClearBlock}
        />
      ) : (
        <TrlTeamSetupView
          teams={teams}
          availableNames={availableNames}
          usedNames={usedNames}
          disabledCoreNames={config.disabledCoreNames}
          customNames={config.customNames}
          createColor={createColor}
          onPickCreateColor={setPickedCreateColor}
          playerNameById={playerNameById}
          unassignedPlayers={unassignedPlayers}
          startLabelByTeam={startLabelByTeam}
          inLobby={inLobby}
          minMembers={minMembers}
          activeBlocks={activeBlocks}
          sabotageEnabled={sabotageEnabled}
          sabotagePlacementByTeam={sabotagePlacementByTeam}
          onCreate={handleCreate}
          onRename={handleRename}
          onRecolor={handleRecolor}
          onDelete={handleDelete}
          onAssign={handleAssign}
          onRemove={handleRemove}
          onSetCaptain={handleSetCaptain}
          onClearBlock={handleClearBlock}
          onSaveNames={handleSaveNames}
        />
      )}

      {disruptions.length > 0 && (
        <DisruptionLogPanel records={disruptions} teamNameById={teamNameById} nodeLabelById={nodeLabelById} />
      )}

      {sabotageEffects.length > 0 && (
        <SabotageLogPanel records={sabotageEffects} teamNameById={teamNameById} nodeLabelById={nodeLabelById} />
      )}
    </div>
  );
}

export default TrlTeamsTab;
